import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, Descriptions, Tag, Table, Button, Space, Alert, Spin, Row, Col, Typography, Divider, Modal, message, App, Radio, Input 
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  DollarOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { fetchInvoiceDetail, Payment, markInvoicePaid, markInvoiceFailed, processPaymentManually, createManualPayment } from '../lib/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { useApp } = App;
const { TextArea } = Input;

const InvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const { modal } = useApp(); // Use Ant Design's hooks API
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [processPaymentOption, setProcessPaymentOption] = useState(true);
  const [showManualPaymentDialog, setShowManualPaymentDialog] = useState(false);
  const [paymentDescription, setPaymentDescription] = useState('');

  const { data: invoiceData, isLoading, error } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn: () => fetchInvoiceDetail(invoiceId!),
    enabled: !!invoiceId,
  });

  // Mutation for creating manual payment
  const createManualPaymentMutation = useMutation({
    mutationFn: (description: string) => createManualPayment(invoiceId!, description),
    onSuccess: (data) => {
      console.log('✅ createManualPayment SUCCESS:', data);
      message.success(data.message + '. Now you can mark it as paid!', 5);
      setShowManualPaymentDialog(false);
      setPaymentDescription('');
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
    },
    onError: (error: any) => {
      console.error('❌ createManualPayment ERROR:', error);
      message.error(`Failed: ${error.response?.data?.error || error.message}`);
      setShowManualPaymentDialog(false);
    }
  });

  // Mutation for marking invoice as paid
  const markPaidMutation = useMutation({
    mutationFn: (processPayment: boolean) => {
      console.log('🔵 mutationFn called, invoiceId:', invoiceId);
      console.log('🔵 processPayment:', processPayment);
      console.log('🔵 Calling markInvoicePaid API...');
      return markInvoicePaid(invoiceId!, undefined, processPayment);
    },
    onSuccess: (data) => {
      console.log('✅ markInvoicePaid SUCCESS:', data);
      
      // Show detailed success message
      const msg = data.message || 'Invoice marked as paid';
      message.success(msg, 5); // Show for 5 seconds
      
      // Close dialog
      setShowMarkPaidDialog(false);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error: any) => {
      console.error('❌ markInvoicePaid ERROR:', error);
      console.error('❌ Error response:', error.response?.data);
      message.error(`Failed to mark as paid: ${error.response?.data?.error || error.message}`);
      setShowMarkPaidDialog(false);
    }
  });

  // Mutation for marking invoice as failed
  const markFailedMutation = useMutation({
    mutationFn: () => markInvoiceFailed(invoiceId!),
    onSuccess: () => {
      message.success('Invoice marked as failed');
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      message.error(`Failed to mark as failed: ${error.response?.data?.error || error.message}`);
    }
  });

  // Mutation for processing payment
  const processPaymentMutation = useMutation({
    mutationFn: (orderId: string) => processPaymentManually(orderId),
    onSuccess: (data) => {
      message.success(`Payment processed! ${data.action_taken}`);
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error: any) => {
      message.error(`Failed to process payment: ${error.response?.data?.error || error.message}`);
    }
  });

  const handleMarkPaid = () => {
    console.log('🔵 handleMarkPaid called');
    console.log('🔵 Invoice ID:', invoiceId);
    
    // Show custom dialog with options
    setProcessPaymentOption(true); // Default to true
    setShowMarkPaidDialog(true);
  };
  
  const confirmMarkPaid = () => {
    console.log('🔵 User confirmed, processPayment:', processPaymentOption);
    markPaidMutation.mutate(processPaymentOption);
  };
  
  const handleCreateManualPayment = () => {
    // Show modal with description input
    setPaymentDescription(`Manual payment for invoice ${invoiceData?.invoice_number || ''}`);
    setShowManualPaymentDialog(true);
  };
  
  const confirmCreateManualPayment = () => {
    createManualPaymentMutation.mutate(paymentDescription);
  };

  const handleMarkFailed = () => {
    const confirmed = window.confirm(
      '⚠️ MARK INVOICE AS FAILED?\n\n' +
      `Invoice ID: ${invoiceId}\n` +
      `This will mark the invoice as failed.\n\n` +
      'Click OK to confirm, or Cancel to abort.'
    );
    
    if (confirmed) {
      console.log('🔵 Mark failed confirmed, calling mutate()...');
      markFailedMutation.mutate();
    } else {
      console.log('🔵 Mark failed cancelled');
      message.info('Action cancelled');
    }
  };

  const handleProcessPayment = (orderId: string) => {
    const confirmed = window.confirm(
      '⚠️ PROCESS PAYMENT MANUALLY?\n\n' +
      `Order ID: ${orderId}\n\n` +
      'This will:\n' +
      '✓ Mark payment as credited\n' +
      '✓ Credit wallet OR activate subscription\n' +
      '✓ Mark invoice as paid\n\n' +
      '⚠️ This action cannot be undone!\n' +
      'Use this for payments that completed but weren\'t processed due to webhook issues.\n\n' +
      'Click OK to confirm, or Cancel to abort.'
    );
    
    if (confirmed) {
      console.log('🔵 Process payment confirmed for order:', orderId);
      processPaymentMutation.mutate(orderId);
    } else {
      console.log('🔵 Process payment cancelled');
      message.info('Action cancelled');
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error"
          description="Failed to load invoice details"
          type="error"
          showIcon
        />
        <Button onClick={() => navigate('/invoices')} style={{ marginTop: '16px' }}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  const getStatusTag = (status: string) => {
    const colors = {
      open: 'blue',
      pending: 'orange',
      paid: 'green',
      failed: 'red',
      expired: 'gray',
    };
    const icons = {
      open: <ClockCircleOutlined />,
      pending: <ClockCircleOutlined />,
      paid: <CheckCircleOutlined />,
      failed: <CloseCircleOutlined />,
      expired: <CloseCircleOutlined />,
    };
    return (
      <Tag color={colors[status as keyof typeof colors]} icon={icons[status as keyof typeof icons]} style={{ fontSize: '14px', padding: '4px 12px' }}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const paymentColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => (
        <Tag color={provider === 'yekpay' ? 'blue' : 'green'}>
          {provider.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'finished' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Credited',
      dataIndex: 'credited',
      key: 'credited',
      render: (credited: boolean) => (
        credited ? <Tag color="success" icon={<CheckCircleOutlined />}>Yes</Tag> : <Tag>No</Tag>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: Payment) => (
        <span>{record.price_amount} {record.price_currency}</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
    },
    {
      title: 'Processed',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (date?: string) => date ? dayjs(date).format('MMM D, YYYY HH:mm') : '-',
    },
  ];

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      {/* Create Manual Payment Dialog */}
      <Modal
        open={showManualPaymentDialog}
        title="Create Manual Payment"
        onOk={confirmCreateManualPayment}
        onCancel={() => setShowManualPaymentDialog(false)}
        okText="Create Payment"
        okButtonProps={{ 
          loading: createManualPaymentMutation.isPending,
          icon: <FileTextOutlined />
        }}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="📝 Manual Payment Record"
            description="Create a payment record for offline payment methods (bank transfer, cash, etc.)"
            type="info"
            showIcon
          />
          
          <div>
            <Text strong>Invoice:</Text> <Text code>{invoiceData?.invoice_number}</Text>
          </div>
          
          <div>
            <Text strong>Amount:</Text> <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>{invoiceData?.total_amount} ZAR</Text>
          </div>
          
          <Divider />
          
          <div>
            <Text strong style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>Payment Description/Notes:</Text>
            <TextArea
              value={paymentDescription}
              onChange={(e) => setPaymentDescription(e.target.value)}
              placeholder="e.g., Bank transfer ref: 12345, Received via: XYZ Bank, Date: 2025-11-08"
              rows={4}
              maxLength={500}
              showCount
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              Add details about how you received the payment (reference numbers, dates, payment method, etc.)
            </Text>
          </div>
          
          <Alert
            message="After creating this payment, use 'Mark as Paid' to credit the wallet"
            type="warning"
            showIcon
          />
        </Space>
      </Modal>

      {/* Mark as Paid Custom Dialog */}
      <Modal
        open={showMarkPaidDialog}
        title="Mark Invoice as Paid"
        onOk={confirmMarkPaid}
        onCancel={() => setShowMarkPaidDialog(false)}
        okText="Confirm"
        okButtonProps={{ 
          loading: markPaidMutation.isPending,
          icon: <CheckCircleOutlined />
        }}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="⚠️ Important Decision"
            description="Choose how you want to mark this invoice as paid."
            type="warning"
            showIcon
          />
          
          <div>
            <Text strong>Invoice ID:</Text> <Text code>{invoiceId}</Text>
          </div>
          
          <div>
            <Text strong>Invoice Type:</Text> <Tag color="blue">{invoiceData?.type}</Tag>
          </div>
          
          <Divider />
          
          <div>
            <Text strong style={{ fontSize: '16px' }}>Select Action:</Text>
            <Radio.Group 
              value={processPaymentOption} 
              onChange={(e) => setProcessPaymentOption(e.target.value)}
              style={{ marginTop: '16px', width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value={true} style={{ display: 'block', padding: '12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                  <div>
                    <Text strong style={{ color: '#52c41a' }}>✅ Mark as Paid + Process Payment (Recommended)</Text>
                    <div style={{ marginTop: '8px', color: '#666' }}>
                      {invoiceData?.type === 'wallet_topup' ? (
                        <>• Marks invoice as paid<br/>• Credits {invoiceData?.total_amount} ZAR to user's wallet</>
                      ) : (
                        <>• Marks invoice as paid<br/>• Activates user's subscription<br/>• Awards bonus credits (if applicable)</>
                      )}
                    </div>
                  </div>
                </Radio>
                
                <Radio value={false} style={{ display: 'block', padding: '12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                  <div>
                    <Text strong style={{ color: '#faad14' }}>⚠️ Only Mark Invoice as Paid (Status Only)</Text>
                    <div style={{ marginTop: '8px', color: '#666' }}>
                      • Only changes invoice status to "paid"<br/>
                      • Does NOT credit wallet or activate subscription<br/>
                      • Use this if payment was already processed separately
                    </div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>
          
          <Alert
            message="This action cannot be easily undone"
            type="info"
            showIcon
          />
        </Space>
      </Modal>

      <Space style={{ marginBottom: '24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invoices')}>
          Back
        </Button>
      </Space>

      <Row gutter={24}>
        <Col span={16}>
          {/* Invoice Details */}
          <Card title={<Space><FileTextOutlined /> Invoice Details</Space>} style={{ marginBottom: '24px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {invoiceData.invoice_number}
                </Title>
                {getStatusTag(invoiceData.status)}
              </div>

              <Descriptions column={2} bordered>
                <Descriptions.Item label="Type">
                  <Tag color="purple">{invoiceData.type.replace('_', ' ').toUpperCase()}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Amount">
                  <strong>{invoiceData.total_amount} {invoiceData.currency}</strong>
                  {invoiceData.zar_amount && <div style={{ fontSize: '12px', color: '#888' }}>{invoiceData.zar_amount} ZAR</div>}
                </Descriptions.Item>
                <Descriptions.Item label="Customer Name">{invoiceData.customer_name || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Customer Email">{invoiceData.customer_email}</Descriptions.Item>
                {invoiceData.subscription_tier && (
                  <>
                    <Descriptions.Item label="Subscription Tier">
                      <Tag color="gold">{invoiceData.subscription_tier.toUpperCase()}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Billing Period">
                      <Tag>{invoiceData.subscription_period?.toUpperCase()}</Tag>
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label="Created">{dayjs(invoiceData.created_at).format('MMMM D, YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Expires">{dayjs(invoiceData.expires_at).format('MMMM D, YYYY HH:mm')}</Descriptions.Item>
                {invoiceData.paid_at && (
                  <Descriptions.Item label="Paid At" span={2}>
                    {dayjs(invoiceData.paid_at).format('MMMM D, YYYY HH:mm')}
                  </Descriptions.Item>
                )}
                {invoiceData.description && (
                  <Descriptions.Item label="Description" span={2}>
                    {invoiceData.description}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {invoiceData.billing_address && Object.keys(invoiceData.billing_address).length > 0 && (
                <>
                  <Divider>Billing Address</Divider>
                  <Descriptions column={2} bordered>
                    {invoiceData.billing_address.first_name && (
                      <Descriptions.Item label="First Name">{invoiceData.billing_address.first_name}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.last_name && (
                      <Descriptions.Item label="Last Name">{invoiceData.billing_address.last_name}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.address && (
                      <Descriptions.Item label="Address" span={2}>{invoiceData.billing_address.address}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.city && (
                      <Descriptions.Item label="City">{invoiceData.billing_address.city}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.postal_code && (
                      <Descriptions.Item label="Postal Code">{invoiceData.billing_address.postal_code}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.country && (
                      <Descriptions.Item label="Country">{invoiceData.billing_address.country}</Descriptions.Item>
                    )}
                    {invoiceData.billing_address.mobile && (
                      <Descriptions.Item label="Mobile">{invoiceData.billing_address.mobile}</Descriptions.Item>
                    )}
                  </Descriptions>
                </>
              )}
            </Space>
          </Card>

          {/* Payment Attempts */}
          <Card title={`Payment Attempts (${invoiceData.payments?.length || 0})`}>
            {invoiceData.payments && invoiceData.payments.length > 0 ? (
              <Table
                columns={paymentColumns}
                dataSource={invoiceData.payments}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <Alert message="No payment attempts yet" type="info" showIcon />
            )}
          </Card>
        </Col>

        <Col span={8}>
          {/* User Info */}
          <Card title={<Space><UserOutlined /> User Information</Space>} style={{ marginBottom: '24px' }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="User ID">{invoiceData.user?.id}</Descriptions.Item>
              {invoiceData.user?.username && (
                <Descriptions.Item label="Username">{invoiceData.user.username}</Descriptions.Item>
              )}
              {invoiceData.user?.telegram_id && (
                <Descriptions.Item label="Telegram ID">{invoiceData.user.telegram_id}</Descriptions.Item>
              )}
              {invoiceData.user?.email && (
                <Descriptions.Item label="Email">{invoiceData.user.email}</Descriptions.Item>
              )}
            </Descriptions>
            <Button 
              type="primary" 
              block 
              style={{ marginTop: '16px' }}
              onClick={() => navigate(`/users/${invoiceData.user_id}`)}
            >
              View User Details
            </Button>
          </Card>

          {/* Actions */}
          <Card title="Admin Actions">
            {invoiceData.status !== 'paid' ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="Manual Status Control"
                  description="Use these buttons to manually change invoice status. Use with caution!"
                  type="warning"
                  showIcon
                />
                
                {/* Show "Create Manual Payment" button if no payment exists */}
                {(!invoiceData.payments || invoiceData.payments.length === 0) && (
                  <>
                    <Alert
                      message="No Payment Record Found"
                      description="This invoice has no payment record. Create a manual payment first if you received payment offline (bank transfer, cash, etc.)."
                      type="info"
                      showIcon
                      style={{ marginTop: '16px' }}
                    />
                    <Button 
                      type="dashed" 
                      icon={<FileTextOutlined />}
                      onClick={handleCreateManualPayment}
                      loading={createManualPaymentMutation.isPending}
                      block
                      style={{ marginTop: '8px' }}
                    >
                      📝 Create Manual Payment Record
                    </Button>
                    <Divider />
                  </>
                )}
                
                <Button 
                  type="primary" 
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    console.log('🔵 BUTTON CLICKED!');
                    handleMarkPaid();
                  }}
                  loading={markPaidMutation.isPending}
                  block
                  style={{ marginTop: '16px' }}
                >
                  Mark as Paid
                </Button>
                
                <Button 
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleMarkFailed}
                  loading={markFailedMutation.isPending}
                  block
                >
                  Mark as Failed
                </Button>

                {invoiceData.payments && invoiceData.payments.some(p => !p.credited && p.status === 'finished') && (
                  <>
                    <Divider />
                    <Alert
                      message="Uncredited Payment Found!"
                      description="There's a finished payment that hasn't been credited. Use the button below to process it."
                      type="error"
                      showIcon
                    />
                    {invoiceData.payments
                      .filter(p => !p.credited && p.status === 'finished')
                      .map(payment => (
                        <Button
                          key={payment.id}
                          type="primary"
                          danger
                          icon={<ThunderboltOutlined />}
                          onClick={() => handleProcessPayment(payment.order_id)}
                          loading={processPaymentMutation.isPending}
                          block
                        >
                          Process Payment {payment.order_id.substring(0, 20)}...
                        </Button>
                      ))}
                  </>
                )}
              </Space>
            ) : (
              <Alert
                message="Invoice Already Paid"
                description="This invoice has been successfully paid."
                type="success"
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceDetail;

